import type { FetchArgs } from '@reduxjs/toolkit/query';

export const TOKEN_REFRESH_SKEW_MS = 30_000;
export const TOKEN_REFRESH_PATH = '/user/token/refresh/';

let accessExpiresAtMs: number | null = null;
let inflightRefresh: Promise<boolean> | null = null;

export function recordAccessExpiry(expiresInSeconds: number): void {
  if (!Number.isFinite(expiresInSeconds) || expiresInSeconds <= 0) {
    accessExpiresAtMs = null;
    return;
  }
  accessExpiresAtMs = Date.now() + expiresInSeconds * 1000;
}

export function clearAccessExpiry(): void {
  accessExpiresAtMs = null;
}

export function getAccessExpiresAtMs(): number | null {
  return accessExpiresAtMs;
}

export function needsProactiveRefresh(now = Date.now()): boolean {
  if (accessExpiresAtMs == null) return true;
  return now >= accessExpiresAtMs - TOKEN_REFRESH_SKEW_MS;
}

export function requestUrl(args: string | FetchArgs): string {
  return typeof args === 'string' ? args : args.url || '';
}

export function requestMethod(args: string | FetchArgs): string {
  if (typeof args === 'string') return 'GET';
  return (args.method || 'GET').toUpperCase();
}

export function isRefreshRequest(args: string | FetchArgs): boolean {
  return requestUrl(args).includes(TOKEN_REFRESH_PATH);
}

export function isAuthBootstrapRequest(args: string | FetchArgs): boolean {
  const url = requestUrl(args);
  return (
    /\/user\/login\/?$/.test(url) ||
    url.includes(TOKEN_REFRESH_PATH) ||
    /\/user\/token\/logout\/?$/.test(url) ||
    url.includes('/2fa/')
  );
}

export function isSafeRetryMethod(method: string): boolean {
  return ['GET', 'HEAD', 'OPTIONS'].includes(method.toUpperCase());
}

export async function withSingleFlightRefresh(
  run: () => Promise<boolean>,
): Promise<boolean> {
  if (inflightRefresh) return inflightRefresh;
  inflightRefresh = (async () => {
    try {
      return await run();
    } finally {
      inflightRefresh = null;
    }
  })();
  return inflightRefresh;
}

/** Test helper */
export function resetTokenSessionForTests(): void {
  accessExpiresAtMs = null;
  inflightRefresh = null;
}
